import React from 'react';
import type { UserPublicRead } from '@models/api';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import { DEFAULT_MAX_FACES, selectGoingFaces, type GoingFacesFilter } from './goingFaces';

export interface GoingFacepileProps {
  /** Attendee preview (event.preview_users), creator first. */
  users?: UserPublicRead[];
  /**
   * Precomputed faces to render EXACTLY, bypassing the internal `selectGoingFaces`.
   * When provided, `users`/`filter`/`maxFaces` are ignored — the caller owns
   * selection. The space rail passes this so the pile is clamped to `going_count`
   * (a probe payload can carry more previews than are going) and the same clamped
   * set feeds both the avatars AND the who's-going copy — they can never disagree —
   * while the filter+slice runs once per cell, not again per 6s rotation tick.
   */
  faces?: UserPublicRead[];
  /** Per-face size classes — the surfaces deliberately differ (e.g. 'h-6 w-6 md:h-7 md:w-7'). */
  faceSizeClasses: string;
  /** Ring color matching the backdrop the pile sits on ('ring-white' / 'ring-mb-bg'). */
  ringClass: string;
  /** Face-selection rule when deriving from `users`. Ignored (and optional) when `faces` is passed. */
  filter?: GoingFacesFilter;
  /**
   * Decorative initials rendered when no real face qualifies (the guest
   * pill's illustrative placeholders — the count next to them is always
   * real). Omit to render nothing instead.
   */
  placeholderInitials?: readonly string[];
  /**
   * Cap on real faces rendered (default 3, the event-detail pile). The pile
   * renders from whatever `users` actually arrived — the backend sends
   * variable previews (≤4 guest / ≤5 authed), so never assume the list length.
   */
  maxFaces?: number;
  /**
   * The full going-set size (`going_count`). When it exceeds the real faces
   * shown, the pile is capped with a `+N` overflow chip. The chip only ever
   * accompanies real faces — with none shown (or only placeholders) it is
   * suppressed and the caller's own count copy carries the number.
   */
  totalCount?: number;
  /** Disc overlap (default '-space-x-2'); tighter piles pass e.g. '-space-x-1.5'. */
  overlapClasses?: string;
  /**
   * Full-information accessible label (e.g. "12 going"). The pile's visuals
   * are decorative compression (§0 a11y), so surfaces whose visible text no
   * longer carries the count MUST pass this; surfaces that still say it in
   * their own copy omit it.
   */
  srLabel?: string;
}

/**
 * Overlapping avatar stack shared by every "who's going / who was there"
 * surface: the event-detail summary, the guest hero's EventGoingPill, the
 * event feed explainer, live pills, and recap cards. Each face renders the
 * profile picture, or an initial on the teal disc; `totalCount` adds a `+N`
 * overflow chip after the faces. The knobs carry the surfaces' deliberate
 * differences — sizes, ring, overlap, filter, cap, placeholders all differ on
 * purpose; don't flatten them.
 */
export const GoingFacepile: React.FC<GoingFacepileProps> = ({
  users,
  faces: providedFaces,
  faceSizeClasses,
  ringClass,
  filter = 'picture-or-name',
  placeholderInitials,
  maxFaces = DEFAULT_MAX_FACES,
  totalCount,
  overlapClasses = '-space-x-2',
  srLabel,
}) => {
  // A caller that precomputed its faces (e.g. clamped to going_count) gets them
  // rendered EXACTLY — no re-derivation; otherwise select from `users` here.
  const faces = providedFaces ?? selectGoingFaces(users, filter, maxFaces);
  const face = `${faceSizeClasses} shrink-0 rounded-full ring-2 ${ringClass}`;
  const initialFace = `${face} flex items-center justify-center bg-mb-teal-light text-xs font-semibold text-mb-ink`;

  const rendered =
    faces.length > 0
      ? faces.map(user => (
          <ImgWithFallback
            key={user.id}
            src={user.profile_picture}
            alt=""
            className={`${face} object-cover`}
            fallback={() => (
              <span aria-hidden="true" className={initialFace}>
                {user.full_name.trim().charAt(0).toUpperCase()}
              </span>
            )}
          />
        ))
      : (placeholderInitials ?? []).map(initial => (
          <span key={initial} aria-hidden="true" className={initialFace}>
            {initial}
          </span>
        ));

  // +N counts what the pile visually folded away: the going set minus the real
  // faces shown. Computed from faces.length (never an assumed preview size) so
  // variable piles stay honest.
  const overflow = faces.length > 0 && totalCount !== undefined ? totalCount - faces.length : 0;

  if (rendered.length === 0) {
    return null;
  }
  return (
    <div className={`flex items-center ${overlapClasses}`}>
      {srLabel ? <span className="sr-only">{srLabel}</span> : null}
      {rendered}
      {overflow > 0 ? (
        // min-w-fit lets a wide count outgrow the face-sized disc into a pill.
        <span aria-hidden="true" className={`${initialFace} min-w-fit px-1 tabular-nums`}>
          +{overflow}
        </span>
      ) : null}
    </div>
  );
};
